import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Context
import DataContext from '../../Contexts/DataContext';
import { useAuth } from '../../Contexts/AuthContext';
import { getProjectDataByGlKey } from '../../Helpers/dataApi';

// Components
import AccountFilterPanel from './AccountFilterPanel';
import TaxonomyPanel from './TaxonomyPanel';
import MonthlyValuesModal from './MonthlyValuesPanel';
import DeleteConfirmationDialog from '../../Components/DeleteConfirmation/DeleteConfirmationDialog';
import BulkEditSummaryDialog from '../../Components/BulkEditSummary/BulkEditSummaryDialog';
import SimulationBanner from '../../Components/SimulationBanner/SimulationBanner';

// Grid configuration
import { getColumnDefs, getGridOptions, getRowId, ActionsCellRenderer, DropdownCellRenderer } from './glKeyGridConfig';
import { validateField } from './glKeyValidation';

// Styles
import './glKeyManagement.scss';

const GLKeyManagement = ({ onClose }) => {
  const context = useContext(DataContext);
  const auth = useAuth();
  const gridRef = useRef(null);
  const taxonomyPanelRef = useRef(null);
  // Guard flag to prevent setDataValue revert from re-triggering onCellValueChanged
  const isRevertingRef = useRef(false);
  const filterRequestIdRef = useRef(0); // incremented on each handleApplyFilter call to detect stale responses

  // State
  const [glKeys, setGlKeys] = useState([]);
  const [filteredGlKeys, setFilteredGlKeys] = useState([]); // Accounts shown in grid after filtering
  const [selectedRows, setSelectedRows] = useState([]);
  const [changedRows, setChangedRows] = useState(new Map());
  const [originalMonthlyValues, setOriginalMonthlyValues] = useState(new Map()); // Store original monthly values by account id
  const [monthlyValuesModalOpen, setMonthlyValuesModalOpen] = useState(false);
  const [selectedGlKeyForEdit, setSelectedGlKeyForEdit] = useState(null);
  const [entities, setEntities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasTaxonomyChanges, setHasTaxonomyChanges] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogItems, setDeleteDialogItems] = useState([]);
  const [deleteDialogConfig, setDeleteDialogConfig] = useState({ title: '', message: '', onConfirm: null });
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [addAccountForm, setAddAccountForm] = useState({
    entity_id: '', entity: '', department_id: '', department_name: '',
    account_id: '', account_name: '', parent_account: '',
    acct_type: '', fixed_or_variable: '', driver: '',
  });
  const [addAccountErrors, setAddAccountErrors] = useState({});
  const [addAccountSaving, setAddAccountSaving] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryDialogData, setSummaryDialogData] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadProjectData();
  }, [context.activeProjectInfo]);

  /**
   * Load project data if not already loaded
   */
  const loadProjectData = async () => {
    if (!context.activeProjectInfo || !context.activeProjectInfo.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Check if project data is already loaded (null = not yet fetched; [] = fetched but empty)
      if (context.activeProjectAttributes?.accountsMetadata == null) {
        // Load accounts via metadata endpoint
        if (auth.activeUser?.accessToken) {
          await context.setActiveProjectAccountsAttribute(
            context.activeProjectInfo.id,
            auth.activeUser.accessToken
          );
        }
      }
      
      // Now load GL Keys and taxonomy data
      await loadGLKeys();
      await loadTaxonomyData();
      
    } catch (err) {
      console.error('Error loading project data:', err);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load GL Keys from context
   */
  const loadGLKeys = async () => {
    if (!context.activeProjectInfo || !context.activeProjectInfo.id) {
      return;
    }

    try {
      // Get accounts from context - use accountsMetadata which has all fields including parent_account
      // Deep-clone so local edits don't mutate DataProvider/context state
      const accountsFromContext = context.activeProjectAttributes?.accountsMetadata || [];
      const accounts = cloneDeep(accountsFromContext);
      setGlKeys(accounts);
      
      // Store original monthly values for each account (for revert functionality)
      const originalValues = new Map();
      accounts.forEach(account => {
        if (account.dates) {
          // Deep clone the dates object and round values
          const roundedDates = {};
          Object.keys(account.dates).forEach(dateKey => {
            const value = account.dates[dateKey];
            roundedDates[dateKey] = value != null ? Math.round(value) : null;
          });
          originalValues.set(account.id, roundedDates);
        }
      });
      setOriginalMonthlyValues(originalValues);
      
    } catch (err) {
      console.error('Error loading GL Keys:', err);
      toast.error('Failed to load GL Keys');
    }
  };

  /**
   * Load taxonomy data (entities, departments, parent accounts, drivers)
   */
  const loadTaxonomyData = async () => {
    if (!context.activeProjectInfo || !context.activeProjectInfo.id) {
      return;
    }

    try {
      // Load entities
      const entitiesData = context.activeProjectAttributes?.entities || [];
      setEntities(entitiesData);

      // Load departments
      const departmentsData = context.activeProjectAttributes?.departments || [];
      setDepartments(departmentsData);

      // Load parent accounts - merge taxonomy parent accounts with those in accountsMetadata.
      // We must call getParentAccounts() here so that newly-created parent accounts (which may
      // not yet be referenced by any account) still appear in the filter/add-account dropdowns.
      const allAccounts = context.activeProjectAttributes?.accountsMetadata || [];
      const accountsParentAccounts = allAccounts
        .map(acc => acc.parent_account)
        .filter(pa => pa && pa.trim() !== '');

      // Fetch taxonomy parent accounts from API so newly-created parent accounts that haven't
      // been assigned to any account yet still appear in the dropdowns. Errors are already
      // swallowed inside _getParentAccounts (returns [] on failure); this function is also
      // wrapped by the outer try-catch below.
      const taxonomyParentAccounts = typeof context.getParentAccounts === 'function'
        ? await context.getParentAccounts()
        : [];

      const taxonomyParentAccountNames = (taxonomyParentAccounts || [])
        .map(parentAccount => {
          if (typeof parentAccount === 'string') {
            return parentAccount;
          }
          // API objects use parent_account_name; local/legacy objects may use name or parent_account.
          return (
            parentAccount?.parent_account_name ||
            parentAccount?.name ||
            parentAccount?.parent_account ||
            ''
          );
        })
        .filter(name => name && name.trim() !== '');

      const uniqueParentAccounts = [...new Set([...accountsParentAccounts, ...taxonomyParentAccountNames])].sort();
      setParentAccounts(uniqueParentAccounts);

      // Load drivers - all accounts (will be filtered in dropdown by entity/dept/acct_type)
      // We need all accounts available so the dropdown can filter them properly
      setDrivers(allAccounts);
    } catch (err) {
      console.error('Error loading taxonomy data:', err);
    }
  };

  /**
   * Handle cell value changed
   */
  const handleCellValueChanged = useCallback((params) => {
    // Ignore change events triggered by our own revert setDataValue calls
    if (isRevertingRef.current) return;

    const { data, colDef, newValue, oldValue } = params;
    
    // Validate first, before marking as changed
    const validation = validateField(colDef.field, newValue, data);
    if (!validation.isValid) {
      // Revert value - set the flag so the resulting change event is ignored.
      // Use try-finally to ensure the flag is always reset even if setDataValue throws.
      isRevertingRef.current = true;
      try {
        params.node.setDataValue(colDef.field, oldValue);
      } finally {
        isRevertingRef.current = false;
      }
      toast.error(validation.error);
      return;
    }

    // Validation passed - now mark as changed
    if (!data._changed) data._changed = {};
    data._changed[colDef.field] = true;
    data._hasChanges = true;
    
    // Update changedRows map
    setChangedRows(prev => {
      const newMap = new Map(prev);
      newMap.set(data.id, data);
      return newMap;
    });
    
    setHasUnsavedChanges(true);
    
    // Refresh row to update styling
    params.api.refreshCells({ rowNodes: [params.node], force: true });
  }, []);

  /**
   * Handle row selection
   */
  const handleRowSelected = useCallback((params) => {
    const selectedNodes = params.api.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    setSelectedRows(selectedData);
  }, []);

  /**
   * Handle grid ready
   */
  const handleGridReady = useCallback((params) => {
    // Grid is ready
  }, []);

  const handleAddNewRow = () => {
    setAddAccountForm({
      entity_id: '', entity: '', department_id: '', department_name: '',
      account_id: '', account_name: '', parent_account: '',
      acct_type: '', fixed_or_variable: '', driver: '',
    });
    setAddAccountErrors({});
    setAddAccountOpen(true);
  };

  const validateAddAccountForm = () => {
    const errors = {};
    if (!addAccountForm.entity_id) errors.entity_id = 'Required';
    else if (!/^\d+$/.test(String(addAccountForm.entity_id).trim())) errors.entity_id = 'Must be numeric';
    if (!addAccountForm.department_id) errors.department_id = 'Required';
    else if (!/^\d+$/.test(String(addAccountForm.department_id).trim())) errors.department_id = 'Must be numeric';
    if (!addAccountForm.account_id) errors.account_id = 'Required';
    else if (!/^\d+$/.test(String(addAccountForm.account_id).trim())) errors.account_id = 'Must be numeric';
    if (!addAccountForm.account_name) errors.account_name = 'Required';
    if (!addAccountForm.acct_type) errors.acct_type = 'Required';
    if (!addAccountForm.fixed_or_variable) errors.fixed_or_variable = 'Required';
    if (!addAccountForm.parent_account) errors.parent_account = 'Required';
    return errors;
  };

  const handleAddAccountSave = async () => {
    const errors = validateAddAccountForm();
    if (Object.keys(errors).length > 0) {
      setAddAccountErrors(errors);
      return;
    }

    setAddAccountSaving(true);
    try {
      // Build dates object with zeros for all project months
      const projectStart = context.activeProjectInfo?.start_date;
      const projectEnd = context.activeProjectInfo?.end_date;
      const dates = {};
      if (projectStart && projectEnd) {
        const start = new Date(`${projectStart}T00:00:00`);
        // Backend stores project dates as the last day of the previous month (e.g., Jan 31 = Feb start).
        // Adding 1 day converts to the first of the represented month.
        start.setDate(start.getDate() + 1);
        const end = new Date(`${projectEnd}T00:00:00`);
        end.setDate(end.getDate() + 1);
        const cur = new Date(start.getFullYear(), start.getMonth(), 1);
        while (cur <= end) {
          const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-01`;
          dates[key] = 0;
          cur.setMonth(cur.getMonth() + 1);
        }
      }

      const payload = {
        ...addAccountForm,
        dates,
      };

      const result = await context.createAccount(payload);
      if (result) {
        toast.success(`Account ${result.gl_key} created successfully`);
        setAddAccountOpen(false);
        // Add the newly created account to the grid data
        setFilteredGlKeys(prev => [...prev, { ...result, _isNew: false }]);
      } else {
        toast.error('Failed to create account. Check that the GL Key is unique.');
      }
    } catch (err) {
      console.error('Error creating account:', err);
      toast.error('Error creating account');
    } finally {
      setAddAccountSaving(false);
    }
  };

  // Departments filtered by selected entity in the add form
  const addFormDepartments = departments.filter(
    d => String(d.entityId) === String(addAccountForm.entity_id)
  );

  /**
   * Handle save all - saves both taxonomy changes and grid changes
   */
  const handleSaveAll = async () => {
    const hasGridChanges = changedRows.size > 0;
    const hasTaxonomyPendingChanges = hasTaxonomyChanges;

    if (!hasGridChanges && !hasTaxonomyPendingChanges) {
      toast.info('No changes to save');
      return;
    }

    setLoading(true);

    try {
      // First, save taxonomy changes if any
      if (hasTaxonomyPendingChanges && taxonomyPanelRef.current?.saveTaxonomyChanges) {
        await taxonomyPanelRef.current.saveTaxonomyChanges();
      }

      // Then, save grid changes if any
      if (hasGridChanges) {
        const rowsToSave = Array.from(changedRows.values());
        
        // Skip new rows - they can't be saved via the update API
        const existingRowsToSave = rowsToSave.filter(row => !row._isNew);
        const newRowCount = rowsToSave.length - existingRowsToSave.length;
        if (newRowCount > 0) {
          toast.warning(`${newRowCount} new row(s) were skipped – creating new GL accounts is not yet supported.`);
        }

        // Separate rows with only monthly value changes from rows with field changes
        const monthlyOnlyUpdates = [];
        const fieldUpdates = [];
        
        existingRowsToSave.forEach(row => {
          const hasFieldChanges = Object.keys(row._changed || {}).some(key => key !== 'dates');
          const hasMonthlyChanges = row._changed?.dates;
          
          if (hasMonthlyChanges && !hasFieldChanges) {
            // Only monthly values changed
            monthlyOnlyUpdates.push({
              gl_key: row.gl_key,
              dates: row.dates
            });
          } else if (hasFieldChanges) {
            // Field changes (may also include monthly changes)
            fieldUpdates.push({
              id: row.id,
              entity_id: row.entity_id,
              entity: row.entity,
              department_id: row.department_id,
              department_name: row.department_name,
              account_id: row.account_id,
              account_name: row.account_name,
              parent_account: row.parent_account,
              acct_type: row.acct_type,
              fixed_or_variable: row.fixed_or_variable,
              driver: row.driver || '',
              dates: row.dates // Include dates in case they also changed
            });
          }
        });

        let totalSuccessful = 0;
        let totalFailed = [];

        // Save field updates using bulk update GL Keys endpoint
        if (fieldUpdates.length > 0) {
          try {
            const result = await context.bulkUpdateGLKeys(fieldUpdates);
            if (result) {
              totalSuccessful += result.successful || 0;
              totalFailed = totalFailed.concat(result.failed || []);
            }
          } catch (err) {
            console.error('Error bulk updating GL Keys:', err);
            toast.error('Error updating accounts');
          }
        }

        // Save monthly-only updates using bulk update monthly values endpoint
        if (monthlyOnlyUpdates.length > 0) {
          try {
            const result = await context.bulkUpdateMonthlyValues(monthlyOnlyUpdates);
            if (result) {
              totalSuccessful += result.successful || 0;
              totalFailed = totalFailed.concat(result.failed || []);
            }
          } catch (err) {
            console.error('Error bulk updating monthly values:', err);
            toast.error('Error updating monthly values');
          }
        }

        // Show summary
        if (totalSuccessful > 0 || totalFailed.length > 0) {
          setSummaryDialogData({
            operation: 'save',
            results: {
              successful: totalSuccessful,
              failed: totalFailed,
            },
          });
          setSummaryDialogOpen(true);
        }

        // Clear changed rows only when all saves succeeded (no failures)
        if (totalSuccessful > 0 && totalFailed.length === 0) {
          setChangedRows(new Map());
          setHasUnsavedChanges(false);
          
          // Reload GL Keys to get fresh data
          await loadGLKeys();
          
          // Reset original monthly values to current values (treat saved values as new baseline)
          const updatedOriginalValues = new Map();
          const currentGlKeys = context.activeProjectAttributes?.accountsMetadata || [];
          currentGlKeys.forEach(account => {
            if (account.dates) {
              const roundedDates = {};
              Object.keys(account.dates).forEach(dateKey => {
                const value = account.dates[dateKey];
                roundedDates[dateKey] = value != null ? Math.round(value) : null;
              });
              updatedOriginalValues.set(account.id, roundedDates);
            }
          });
          setOriginalMonthlyValues(updatedOriginalValues);
          
          toast.success(`Successfully saved ${totalSuccessful} account(s)`);
        } else if (totalSuccessful > 0) {
          // Partial success - reload but keep failed rows as unsaved so the user can retry
          await loadGLKeys();
          toast.warning(`Saved ${totalSuccessful} account(s), but ${totalFailed.length} failed. Please review and retry the failed rows.`);
        }
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      toast.error('Error saving changes');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle discard changes - discards both grid and taxonomy changes
   */
  const handleDiscardChanges = () => {
    setDeleteDialogConfig({
      title: 'Discard Changes',
      message: 'Are you sure you want to discard all unsaved changes (including taxonomy changes)?',
      confirmLabel: 'Discard',
      isDanger: false,
      onConfirm: () => {
        setDeleteDialogOpen(false);
        setChangedRows(new Map());
        setHasUnsavedChanges(false);
        loadGLKeys();
        if (taxonomyPanelRef.current?.discardTaxonomyChanges) {
          taxonomyPanelRef.current.discardTaxonomyChanges(true);
        }
        toast.info('All changes discarded');
      },
    });
    setDeleteDialogItems([]);
    setDeleteDialogOpen(true);
  };

  /**
   * Handle delete single GL Key
   */
  const handleDeleteGLKey = async (glKey) => {
    // Check dependencies
    try {
      const dependencies = await context.checkGLKeyDependencies(glKey.gl_key);
      
      if (!dependencies) {
        toast.error('Failed to check dependencies. Please try again.');
        return;
      }

      if (dependencies.hasDependencies) {
        setDeleteDialogConfig({
          title: 'Delete Account',
          message: null,
          confirmLabel: 'Delete Anyway',
          onConfirm: () => {
            setDeleteDialogOpen(false);
            performDelete([glKey.id]);
          },
        });
        setDeleteDialogItems([{
          gl_key: glKey.gl_key,
          account_name: glKey.account_name,
          dependencies: dependencies.dependencies,
        }]);
        setDeleteDialogOpen(true);
      } else {
        setDeleteDialogConfig({
          title: 'Delete Account',
          message: `Delete account ${glKey.gl_key} – ${glKey.account_name}?`,
          confirmLabel: 'Delete',
          onConfirm: () => {
            setDeleteDialogOpen(false);
            performDelete([glKey.id]);
          },
        });
        setDeleteDialogItems([]);
        setDeleteDialogOpen(true);
      }
    } catch (err) {
      console.error('Error checking dependencies:', err);
      toast.error('Error checking dependencies');
    }
  };

  /**
   * Handle bulk delete
   */
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Please select accounts to delete');
      return;
    }

    const accountIds = selectedRows.map(row => row.id);
    await performDelete(accountIds);
  };

  /**
   * Perform delete operation
   */
  const performDelete = async (accountIds) => {
    try {
      const result = await context.bulkDeleteGLKeys(accountIds);
      
      if (!result) {
        toast.error('Delete operation failed. Please try again.');
        return;
      }

      setSummaryDialogData({
        operation: 'delete',
        results: {
          successful: result.deleted?.length || 0,
          failed: result.failed || [],
        },
      });
      setSummaryDialogOpen(true);

      // Update local state immediately from the delete result so deleted rows
      // disappear without waiting for context setState to settle asynchronously.
      if (result.deleted?.length > 0) {
        const deletedSet = new Set(result.deleted);
        setGlKeys(prev => prev.filter(row => !deletedSet.has(row.id)));
        setFilteredGlKeys(prev => prev.filter(row => !deletedSet.has(row.id)));
      }
      await loadGLKeys();
      setSelectedRows([]);
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error('Error deleting account');
    }
  };

  /**
   * Handle edit monthly values - opens modal
   */
  const handleEditMonthlyValues = (rowData) => {
    setSelectedGlKeyForEdit(rowData);
    setMonthlyValuesModalOpen(true);
  };

  /**
   * Handle close monthly values modal
   */
  const handleCloseMonthlyValuesModal = () => {
    setMonthlyValuesModalOpen(false);
    setSelectedGlKeyForEdit(null);
  };

  /**
   * Handle monthly value change
   */
  const handleMonthlyValueChange = (rowId, month, value) => {
    // Update the row data and track changes atomically to avoid stale state
    let updatedRow = null;
    setFilteredGlKeys(prev => {
      return prev.map(row => {
        if (row.id === rowId) {
          updatedRow = {
            ...row,
            dates: {
              ...row.dates,
              [month]: value,
            },
            _hasChanges: true,
            _changed: {
              ...row._changed,
              dates: true, // Mark dates as changed
            },
          };
          return updatedRow;
        }
        return row;
      });
    });

    // Also update glKeys state
    setGlKeys(prev => {
      return prev.map(row => {
        if (row.id === rowId) {
          return {
            ...row,
            dates: {
              ...row.dates,
              [month]: value,
            },
            _hasChanges: true,
            _changed: {
              ...row._changed,
              dates: true,
            },
          };
        }
        return row;
      });
    });

    // Track as changed — use updatedRow derived above to avoid stale closure
    if (updatedRow) {
      setChangedRows(prev => {
        const newMap = new Map(prev);
        newMap.set(rowId, updatedRow);
        return newMap;
      });
      setHasUnsavedChanges(true);
    }

    // Refresh the grid to show the yellow border
    if (gridRef.current && gridRef.current.api) {
      const rowNode = gridRef.current.api.getRowNode(String(rowId));
      if (rowNode) {
        gridRef.current.api.refreshCells({ rowNodes: [rowNode], force: true });
      }
    }
  };

  /**
   * Handle revert monthly value for a specific month
   */
  const handleRevertMonthlyValue = (rowId, month) => {
    const originalValues = originalMonthlyValues.get(rowId);
    if (!originalValues) return;

    const originalValue = originalValues[month];

    // Update filteredGlKeys and check if all months are back to original in one pass
    setFilteredGlKeys(prev => {
      return prev.map(row => {
        if (row.id !== rowId) return row;

        const updatedDates = { ...row.dates, [month]: originalValue };
        const allMonthsOriginal = Object.keys(updatedDates).every(m => updatedDates[m] === originalValues[m]);

        if (allMonthsOriginal) {
          const newChanged = { ...row._changed };
          delete newChanged.dates;
          return { ...row, dates: updatedDates, _changed: newChanged, _hasChanges: Object.keys(newChanged).length > 0 };
        }
        return { ...row, dates: updatedDates };
      });
    });

    // Also update glKeys state
    setGlKeys(prev => {
      return prev.map(row => {
        if (row.id !== rowId) return row;

        const updatedDates = { ...row.dates, [month]: originalValue };
        const allMonthsOriginal = Object.keys(updatedDates).every(m => updatedDates[m] === originalValues[m]);

        if (allMonthsOriginal) {
          const newChanged = { ...row._changed };
          delete newChanged.dates;
          return { ...row, dates: updatedDates, _changed: newChanged, _hasChanges: Object.keys(newChanged).length > 0 };
        }
        return { ...row, dates: updatedDates };
      });
    });

    // Remove from changed rows if no other changes; derive hasUnsavedChanges from new map
    setChangedRows(prev => {
      const newMap = new Map(prev);
      const changedRow = newMap.get(rowId);
      if (changedRow) {
        const updatedDates = { ...changedRow.dates, [month]: originalValue };
        const allMonthsOriginal = Object.keys(updatedDates).every(m => updatedDates[m] === originalValues[m]);
        if (allMonthsOriginal) {
          const hasOtherChanges = Object.keys(changedRow._changed || {}).some(key => key !== 'dates');
          if (!hasOtherChanges) {
            newMap.delete(rowId);
          }
        }
      }
      setHasUnsavedChanges(newMap.size > 0);
      return newMap;
    });

    // Refresh the grid
    if (gridRef.current && gridRef.current.api) {
      const rowNode = gridRef.current.api.getRowNode(String(rowId));
      if (rowNode) {
        gridRef.current.api.refreshCells({ rowNodes: [rowNode], force: true });
      }
    }
  };

  /**
   * Handle revert all monthly values for an account
   */
  const handleRevertAllMonthlyValues = (rowId) => {
    const originalValues = originalMonthlyValues.get(rowId);
    if (!originalValues) return;

    // Update the row data with all original values
    setFilteredGlKeys(prev => {
      return prev.map(row => {
        if (row.id === rowId) {
          const newChanged = { ...row._changed };
          delete newChanged.dates;
          return {
            ...row,
            dates: { ...originalValues },
            _changed: newChanged,
            _hasChanges: Object.keys(newChanged).length > 0,
          };
        }
        return row;
      });
    });

    // Also update glKeys state
    setGlKeys(prev => {
      return prev.map(row => {
        if (row.id === rowId) {
          const newChanged = { ...row._changed };
          delete newChanged.dates;
          return {
            ...row,
            dates: { ...originalValues },
            _changed: newChanged,
            _hasChanges: Object.keys(newChanged).length > 0,
          };
        }
        return row;
      });
    });

    // Remove from changed rows if no other changes; derive hasUnsavedChanges from new map
    setChangedRows(prev => {
      const newMap = new Map(prev);
      const changedRow = newMap.get(rowId);
      if (changedRow) {
        const hasOtherChanges = Object.keys(changedRow._changed || {}).some(key => key !== 'dates');
        if (!hasOtherChanges) {
          newMap.delete(rowId);
        }
      }
      setHasUnsavedChanges(newMap.size > 0);
      return newMap;
    });

    // Refresh the grid
    if (gridRef.current && gridRef.current.api) {
      const rowNode = gridRef.current.api.getRowNode(String(rowId));
      if (rowNode) {
        gridRef.current.api.refreshCells({ rowNodes: [rowNode], force: true });
      }
    }
  };

  /**
   * Handle apply filter - load selected accounts into grid, fetching monthly values lazily
   */
  const handleApplyFilter = useCallback(async (selectedAccounts) => {
    if (!selectedAccounts || selectedAccounts.length === 0) return;

    // Stamp this invocation so we can detect stale in-flight responses
    // (e.g. when the user applies a new filter before the previous fetch finishes).
    const requestId = ++filterRequestIdRef.current;

    // Clone to avoid mutating context-backed objects via grid edits.
    // Initialize dates: {} on each clone so monthly-value flows never receive undefined dates.
    const clonedAccounts = cloneDeep(selectedAccounts);
    clonedAccounts.forEach(account => { if (!account.dates) account.dates = {}; });

    // Show accounts immediately with a loading indicator on dates
    setFilteredGlKeys(clonedAccounts);
    toast.info(`Loading ${clonedAccounts.length} account(s)...`);

    // Fetch monthly values for each account in parallel
    const projectId = context.activeProjectInfo?.id;
    const accessToken = auth.activeUser?.accessToken;

    if (!projectId || !accessToken) {
      toast.success(`Loaded ${clonedAccounts.length} account(s) into grid`);
      return;
    }

    try {
      const CONCURRENCY_LIMIT = 5;
      const results = [];
      for (let i = 0; i < clonedAccounts.length; i += CONCURRENCY_LIMIT) {
        const chunk = clonedAccounts.slice(i, i + CONCURRENCY_LIMIT);
        const chunkResults = await Promise.allSettled(
          chunk.map(account =>
            getProjectDataByGlKey(projectId, account.gl_key, accessToken)
          )
        );
        results.push(...chunkResults);
      }

      const withDates = clonedAccounts.map((account, i) => {
        const result = results[i];
        if (result.status === 'fulfilled' && result.value?.data?.dates) {
          return { ...account, dates: result.value.data.dates };
        }
        // Keep the empty dates object initialized above; dates fetch failed or returned no data
        return account;
      });

      const failedCount = results.filter(r => r.status !== 'fulfilled' || !r.value?.data?.dates).length;
      if (failedCount > 0) {
        toast.warning(`${failedCount} account(s) could not load monthly values and will show empty data.`);
      }

      // Guard against stale responses: if another filter was applied while this one was
      // in-flight, discard the result entirely so old rows and baselines are not updated.
      if (requestId !== filterRequestIdRef.current) return;

      // Store original monthly values for revert functionality.
      // Only record a baseline for accounts whose fetch succeeded (non-empty dates),
      // so "revert" doesn't restore an empty placeholder from a failed fetch.
      setOriginalMonthlyValues(prev => {
        const originalValues = new Map(prev);
        withDates.forEach(account => {
          if (account.dates && Object.keys(account.dates).length > 0 && !originalValues.has(account.id)) {
            const roundedDates = {};
            Object.keys(account.dates).forEach(dateKey => {
              const val = account.dates[dateKey];
              roundedDates[dateKey] = val != null ? Math.round(val) : null;
            });
            originalValues.set(account.id, roundedDates);
          }
        });
        return originalValues;
      });

      // Merge the fetched dates into the current row state by id instead of replacing
      // the entire array, so any in-progress edits the user made while data was loading
      // are preserved. Only update rows that still exist in prev; rows removed during
      // loading (e.g. by user deletion) are intentionally left out.

      const datesById = new Map(withDates.map(a => [a.id, a.dates]));
      setFilteredGlKeys(prev =>
        prev.map(row => {
          const dates = datesById.get(row.id);
          return dates !== undefined ? { ...row, dates } : row;
        })
      );
      toast.success(`Loaded ${withDates.length} account(s) into grid`);
    } catch (err) {
      console.error('Error fetching monthly values:', err);
      toast.warning(`Loaded ${clonedAccounts.length} account(s) into grid, but monthly values failed to load`);
    }
  }, [context.activeProjectInfo, auth.activeUser]);

  /**
   * Handle taxonomy change (reload data and refresh grid)
   */
  const handleTaxonomyChange = async () => {
    await loadGLKeys();
    await loadTaxonomyData();
    
    // Refresh the grid to update dropdowns with new taxonomy data
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  };

  // Grid context for cell renderers
  const gridContext = {
    entities,
    departments,
    parentAccounts,
    drivers,
    onEditMonthly: handleEditMonthlyValues,
    onDelete: handleDeleteGLKey,
  };

  // Column definitions
  const columnDefs = getColumnDefs(gridContext);

  // Grid options
  const gridOptions = getGridOptions({
    onCellValueChanged: handleCellValueChanged,
    onRowSelected: handleRowSelected,
    onGridReady: handleGridReady,
  });

  return (
    <Box className="gl-key-management" sx={{ width: '100%', bgcolor: 'white' }}>
      <Box sx={{ p: 3 }}>
        <SimulationBanner />
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">GL Key Management</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
            {hasUnsavedChanges && (
              <Button
                variant="outlined"
                onClick={handleDiscardChanges}
                disabled={loading}
              >
                Discard All Changes
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={(!hasUnsavedChanges && !hasTaxonomyChanges) || loading}
            >
              Save All
            </Button>
          </Box>
        </Box>
  
        {/* ── Taxonomy Section ── */}
        <Box sx={{
          mb: 3,
          border: '1px solid #546e7a',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{
            px: 2,
            py: 1,
            bgcolor: '#546e7a',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, letterSpacing: 0.5 }}>
              Taxonomy Management
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              — Entities, Departments &amp; Parent Accounts
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: '#f5f7f8' }}>
            <TaxonomyPanel
              ref={taxonomyPanelRef}
              projectId={context.activeProjectInfo?.id}
              onTaxonomyChange={handleTaxonomyChange}
              hasPendingChanges={hasTaxonomyChanges}
              onPendingChangesUpdate={setHasTaxonomyChanges}
            />
          </Box>
        </Box>

        {/* ── GL Key Data Section ── */}
        <Box sx={{
          border: '1px solid #546e7a',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{
            px: 2,
            py: 1,
            bgcolor: '#546e7a',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, letterSpacing: 0.5 }}>
              GL Key Data
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              — Filter &amp; Edit Account Records
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: '#f5f7f8' }}>
            {/* Account Filter Panel */}
            <AccountFilterPanel onApplyFilter={handleApplyFilter} />

            {/* Action Bar */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNewRow}
                disabled={loading}
              >
                Add Account
              </Button>
              {selectedRows.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleBulkDelete}
                  disabled={loading}
                >
                  Delete Selected ({selectedRows.length})
                </Button>
              )}
            </Box>

            {/* Info note about unsaved changes */}
            {hasUnsavedChanges && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3cd', borderRadius: 1, borderLeft: '3px solid #ffc107' }}>
                <Typography variant="body2" color="text.secondary">
                  Accounts with a yellow left border have unsaved changes. Click "Save All" to commit your changes.
                </Typography>
              </Box>
            )}

            {/* AG Grid */}
            <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : filteredGlKeys.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body1" color="textSecondary">
                Use the filter above to select accounts to edit
              </Typography>
            </Box>
          ) : (
            <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
              <AgGridReact
                ref={gridRef}
                rowData={filteredGlKeys}
                columnDefs={columnDefs}
                {...gridOptions}
                getRowId={getRowId}
                context={gridContext}
                components={{
                  actionsCellRenderer: ActionsCellRenderer,
                  dropdownCellRenderer: DropdownCellRenderer,
                }}
              />
            </div>
          )}
            </Box>
          </Box>
        </Box>

        {/* Monthly Values Modal */}
        <MonthlyValuesModal
          open={monthlyValuesModalOpen}
          glKey={selectedGlKeyForEdit}
          originalValues={selectedGlKeyForEdit ? originalMonthlyValues.get(selectedGlKeyForEdit.id) : null}
          projectDateRange={{
            start: context.activeProjectInfo?.start_date,
            end: context.activeProjectInfo?.end_date,
          }}
          onValueChange={(month, value) => {
            if (selectedGlKeyForEdit) {
              handleMonthlyValueChange(selectedGlKeyForEdit.id, month, value);
            }
          }}
          onRevertMonth={(month) => {
            if (selectedGlKeyForEdit) {
              handleRevertMonthlyValue(selectedGlKeyForEdit.id, month);
            }
          }}
          onRevertAll={() => {
            if (selectedGlKeyForEdit) {
              handleRevertAllMonthlyValues(selectedGlKeyForEdit.id);
            }
          }}
          onClose={handleCloseMonthlyValuesModal}
        />
  
        {/* Add Account Modal */}
        <Dialog open={addAccountOpen} onClose={() => setAddAccountOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Account</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* Entity */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={!!addAccountErrors.entity_id}>
                  <InputLabel>Entity *</InputLabel>
                  <Select
                    value={addAccountForm.entity_id}
                    label="Entity *"
                    onChange={e => {
                      const ent = entities.find(en => String(en.entity_id) === String(e.target.value));
                      setAddAccountForm(f => ({
                        ...f,
                        entity_id: e.target.value,
                        entity: ent?.entity_name || '',
                        department_id: '',
                        department_name: '',
                      }));
                      setAddAccountErrors(err => ({ ...err, entity_id: '' }));
                    }}
                  >
                    {entities.map(e => (
                      <MenuItem key={e.entity_id} value={e.entity_id}>
                        {e.entity_id} – {e.entity_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {addAccountErrors.entity_id && <Typography variant="caption" color="error">{addAccountErrors.entity_id}</Typography>}
                </FormControl>
              </Grid>

              {/* Department */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={!!addAccountErrors.department_id} disabled={!addAccountForm.entity_id}>
                  <InputLabel>Department *</InputLabel>
                  <Select
                    value={addAccountForm.department_id}
                    label="Department *"
                    onChange={e => {
                      const dept = addFormDepartments.find(d => String(d.departmentId) === String(e.target.value));
                      setAddAccountForm(f => ({
                        ...f,
                        department_id: e.target.value,
                        department_name: dept?.departmentName || '',
                      }));
                      setAddAccountErrors(err => ({ ...err, department_id: '' }));
                    }}
                  >
                    {addFormDepartments.map(d => (
                      <MenuItem key={d.departmentId} value={d.departmentId}>
                        {d.departmentId} – {d.departmentName}
                      </MenuItem>
                    ))}
                  </Select>
                  {addAccountErrors.department_id && <Typography variant="caption" color="error">{addAccountErrors.department_id}</Typography>}
                </FormControl>
              </Grid>

              {/* Account ID */}
              <Grid item xs={6}>
                <TextField
                  label="Account ID *"
                  size="small"
                  fullWidth
                  value={addAccountForm.account_id}
                  onChange={e => {
                    setAddAccountForm(f => ({ ...f, account_id: e.target.value }));
                    setAddAccountErrors(err => ({ ...err, account_id: '' }));
                  }}
                  error={!!addAccountErrors.account_id}
                  helperText={addAccountErrors.account_id}
                />
                <Typography variant="caption" color="text.secondary">
                  GL Key: {addAccountForm.entity_id || '?'}-{addAccountForm.department_id || '?'}-{addAccountForm.account_id || '?'}
                </Typography>
              </Grid>

              {/* Account Name */}
              <Grid item xs={6}>
                <TextField
                  label="Account Name *"
                  size="small"
                  fullWidth
                  value={addAccountForm.account_name}
                  onChange={e => {
                    setAddAccountForm(f => ({ ...f, account_name: e.target.value }));
                    setAddAccountErrors(err => ({ ...err, account_name: '' }));
                  }}
                  error={!!addAccountErrors.account_name}
                  helperText={addAccountErrors.account_name}
                />
              </Grid>

              {/* Parent Account */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={!!addAccountErrors.parent_account}>
                  <InputLabel>Parent Account *</InputLabel>
                  <Select
                    value={addAccountForm.parent_account}
                    label="Parent Account *"
                    onChange={e => {
                      setAddAccountForm(f => ({ ...f, parent_account: e.target.value }));
                      setAddAccountErrors(err => ({ ...err, parent_account: '' }));
                    }}
                  >
                    {parentAccounts.map((pa, i) => (
                      <MenuItem key={i} value={pa}>{pa}</MenuItem>
                    ))}
                  </Select>
                  {addAccountErrors.parent_account && <Typography variant="caption" color="error">{addAccountErrors.parent_account}</Typography>}
                </FormControl>
              </Grid>

              {/* Acct Type */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={!!addAccountErrors.acct_type}>
                  <InputLabel>Account Type *</InputLabel>
                  <Select
                    value={addAccountForm.acct_type}
                    label="Account Type *"
                    onChange={e => {
                      setAddAccountForm(f => ({ ...f, acct_type: e.target.value }));
                      setAddAccountErrors(err => ({ ...err, acct_type: '' }));
                    }}
                  >
                    {['Revenue', 'Expense', 'Deduction', 'Other Revenue', 'Asset', 'Liability', 'Equity', 'Stat', 'P&L'].map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                  {addAccountErrors.acct_type && <Typography variant="caption" color="error">{addAccountErrors.acct_type}</Typography>}
                </FormControl>
              </Grid>

              {/* Fixed/Variable */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={!!addAccountErrors.fixed_or_variable}>
                  <InputLabel>Fixed/Variable *</InputLabel>
                  <Select
                    value={addAccountForm.fixed_or_variable}
                    label="Fixed/Variable *"
                    onChange={e => {
                      setAddAccountForm(f => ({ ...f, fixed_or_variable: e.target.value }));
                      setAddAccountErrors(err => ({ ...err, fixed_or_variable: '' }));
                    }}
                  >
                    {['Fixed', 'Variable', 'Stat'].map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                  {addAccountErrors.fixed_or_variable && <Typography variant="caption" color="error">{addAccountErrors.fixed_or_variable}</Typography>}
                </FormControl>
              </Grid>

              {/* Driver (optional) */}
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Driver (optional)</InputLabel>
                  <Select
                    value={addAccountForm.driver}
                    label="Driver (optional)"
                    onChange={e => setAddAccountForm(f => ({ ...f, driver: e.target.value }))}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {drivers
                      .filter(d => String(d.entity_id) === String(addAccountForm.entity_id) && d.acct_type === 'Stat')
                      .map(d => (
                        <MenuItem key={d.gl_key} value={d.gl_key}>
                          {d.gl_key} – {d.account_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddAccountOpen(false)} color="inherit" disabled={addAccountSaving}>
              Cancel
            </Button>
            <Button onClick={handleAddAccountSave} variant="contained" disabled={addAccountSaving}>
              {addAccountSaving ? 'Creating...' : 'Create Account'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete / Discard Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title={deleteDialogConfig.title}
          message={deleteDialogConfig.message}
          confirmLabel={deleteDialogConfig.confirmLabel}
          isDanger={deleteDialogConfig.isDanger !== false}
          items={deleteDialogItems}
          onConfirm={deleteDialogConfig.onConfirm || (() => setDeleteDialogOpen(false))}
          onCancel={() => setDeleteDialogOpen(false)}
        />
  
        {/* Bulk Edit Summary Dialog */}
        <BulkEditSummaryDialog
          open={summaryDialogOpen}
          operation={summaryDialogData?.operation || 'save'}
          results={summaryDialogData?.results || { successful: 0, failed: [] }}
          onClose={() => setSummaryDialogOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default GLKeyManagement;
